import React, { useState, useEffect } from 'react';
import { CheckCircle, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import desmancheService from '@/services/desmanche.service';

const FinalizarDesmancheModal = ({ open, onClose, onSuccess, desmanche }) => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [observaciones, setObservaciones] = useState('');
    
    // Estado para cada prenda con sus cantidades recuperadas
    const [prendasDetalle, setPrendasDetalle] = useState([]);

    // Inicializar el estado cuando se abre el modal
    useEffect(() => {
        if (open && desmanche) {
            const detalleInicial = desmanche.prendas.map(prenda => ({
                id: prenda.id,
                nombre: prenda.nombre,
                cantidadTotal: prenda.cantidad,
                cantidadRecuperada: 0,
                cantidadNoRecuperada: prenda.cantidad,
            }));
            setPrendasDetalle(detalleInicial);
        }
    }, [open, desmanche]);

    // Manejar cambio en cantidad recuperada de una prenda
    const handleRecuperadaChange = (index, value) => {
        const numValue = Number(value);
        const prenda = prendasDetalle[index];

        // Validar que no supere el total de esa prenda
        if (numValue > prenda.cantidadTotal) {
            toast({
                title: 'Error',
                description: `La cantidad recuperada no puede superar el total (${prenda.cantidadTotal}) para ${prenda.nombre}`,
                variant: 'destructive',
            });
            return;
        }

        if (numValue < 0) {
            return;
        }

        // Actualizar el estado
        const nuevasDetalle = [...prendasDetalle];
        nuevasDetalle[index] = {
            ...prenda,
            cantidadRecuperada: numValue,
            cantidadNoRecuperada: prenda.cantidadTotal - numValue,
        };
        setPrendasDetalle(nuevasDetalle);
    };

    // Calcular totales
    const totalRecuperadas = prendasDetalle.reduce((sum, p) => sum + p.cantidadRecuperada, 0);
    const totalNoRecuperadas = prendasDetalle.reduce((sum, p) => sum + p.cantidadNoRecuperada, 0);

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);
        try {
            await desmancheService.finalizarDesmanche(desmanche._id, {
                prendasRecuperadas: totalRecuperadas,
                prendasNoRecuperadas: totalNoRecuperadas,
                prendasDetalle: prendasDetalle,
                observaciones: observaciones,
            });
            onSuccess();
        } catch (error) {
            console.error('Error finalizando desmanche:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'No se pudo finalizar el desmanche',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        Finalizar Proceso de Desmanche
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Información del proceso */}
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <span className="text-xs font-medium text-gray-600">Edificio:</span>
                                <p className="text-sm font-semibold">{desmanche.edificio.nombre}</p>
                            </div>
                            <div>
                                <span className="text-xs font-medium text-gray-600">Tipo de Mancha:</span>
                                <p className="text-sm font-semibold capitalize">{desmanche.tipoMancha.replace('_', ' ')}</p>
                            </div>
                            <div>
                                <span className="text-xs font-medium text-gray-600">Total de Prendas:</span>
                                <p className="text-sm font-bold text-orange-700">{desmanche.totalPrendas}</p>
                            </div>
                        </div>
                    </div>

                    {/* Tabla de Prendas */}
                    <div>
                        <Label className="text-base font-semibold mb-3 block">
                            Marcar Prendas Recuperadas por Tipo
                        </Label>
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-100 border-b">
                                    <tr>
                                        <th className="text-left p-3 text-sm font-semibold">Prenda</th>
                                        <th className="text-center p-3 text-sm font-semibold w-32">Total</th>
                                        <th className="text-center p-3 text-sm font-semibold w-40">Recuperadas</th>
                                        <th className="text-center p-3 text-sm font-semibold w-40">No Recuperadas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prendasDetalle.map((prenda, index) => (
                                        <tr key={prenda.id} className="border-b hover:bg-gray-50">
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <Package className="h-4 w-4 text-gray-400" />
                                                    <span className="font-medium">{prenda.nombre}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-center">
                                                <span className="inline-flex items-center justify-center w-12 h-8 bg-blue-100 text-blue-700 rounded font-semibold">
                                                    {prenda.cantidadTotal}
                                                </span>
                                            </td>
                                            <td className="p-3">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max={prenda.cantidadTotal}
                                                    value={prenda.cantidadRecuperada}
                                                    onChange={(e) => handleRecuperadaChange(index, e.target.value)}
                                                    className="text-center font-semibold"
                                                    required
                                                />
                                            </td>
                                            <td className="p-3 text-center">
                                                <span className={`inline-flex items-center justify-center w-12 h-8 rounded font-semibold ${
                                                    prenda.cantidadNoRecuperada === 0 
                                                        ? 'bg-green-100 text-green-700' 
                                                        : 'bg-red-100 text-red-700'
                                                }`}>
                                                    {prenda.cantidadNoRecuperada}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50 border-t-2">
                                    <tr>
                                        <td className="p-3 font-bold">TOTALES</td>
                                        <td className="p-3 text-center">
                                            <span className="inline-flex items-center justify-center w-12 h-8 bg-blue-200 text-blue-800 rounded font-bold">
                                                {desmanche.totalPrendas}
                                            </span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className="inline-flex items-center justify-center w-12 h-8 bg-green-200 text-green-800 rounded font-bold">
                                                {totalRecuperadas}
                                            </span>
                                        </td>
                                        <td className="p-3 text-center">
                                            <span className="inline-flex items-center justify-center w-12 h-8 bg-red-200 text-red-800 rounded font-bold">
                                                {totalNoRecuperadas}
                                            </span>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            * Ingrese la cantidad recuperada para cada tipo de prenda. Las no recuperadas se calculan automáticamente.
                        </p>
                    </div>

                    {/* Observaciones */}
                    <div>
                        <Label htmlFor="observaciones">Observaciones Finales</Label>
                        <Textarea
                            id="observaciones"
                            value={observaciones}
                            onChange={(e) => setObservaciones(e.target.value)}
                            placeholder="Observaciones sobre el resultado del proceso de desmanche..."
                            rows={3}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {loading ? 'Finalizando...' : 'Finalizar Desmanche'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default FinalizarDesmancheModal;
